/**
 * Decision logic for the SegmentationAnnotationPanel sync effect.
 *
 * The effect mirrors the /annotations query into a Jotai atom. Two
 * properties have to hold simultaneously:
 *
 * 1. While the user is mid-draw (`isSegmentationActive=true`), no atom
 *    write should happen — a stale refetch landing mid-draw would
 *    overwrite the optimistic onSuccess entry.
 *
 * 2. The watermark must still advance while skipping, otherwise the
 *    queued refetch's `updatedAt` is "fresh" the moment isSegmentationActive
 *    flips false (i.e. right after Save) and overwrites the optimistic
 *    record with pre-save data — the just-saved annotation disappears.
 *
 * This helper returns the action the effect should take. Pure function so
 * the gating logic is unit-testable without React/Jotai/Query.
 */
export type SyncAction =
  | { kind: "skip" } // no change, leave watermark alone
  | { kind: "advance-only" } // bump watermark, don't apply data
  | { kind: "apply" }; // bump watermark + apply data

export function decideSync(args: {
  isSegmentationActive: boolean;
  updatedAt: number | undefined;
  lastSyncedUpdatedAt: number;
  hasAnnotations: boolean;
}): SyncAction {
  const { isSegmentationActive, updatedAt, lastSyncedUpdatedAt, hasAnnotations } = args;
  if (isSegmentationActive) {
    return updatedAt ? { kind: "advance-only" } : { kind: "skip" };
  }
  if (!updatedAt || updatedAt === lastSyncedUpdatedAt) return { kind: "skip" };
  if (!hasAnnotations) return { kind: "skip" };
  return { kind: "apply" };
}
